import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from 'prop-types';

import { Grid, Typography, Accordion, AccordionDetails, AccordionSummary } from "@material-ui/core";

import { APP_ROUTES } from "../../../config/constants";
import { selectAllAreas, selectAllRegions } from "../../../config/redux/configurationsSlice";
import { getCoordinatesFromAddress, getFullAddress, getRegionNameFromRegionId } from "../../../utils/helperFunctions";
import { is_basic_details_edit_visible } from "../CompleteSetupUtils";
import { ViewBasicDetailNameAndValue } from "../CompleteSetupViewUtils";
import { isArrayValid, isEmptyOrNull } from "../../../utils/DataUtils";
import { STORE_SERVICE_LABELS } from "../../common/App/app-constant";

import ExpandMoreIcon from "../../assets/svg/ExpandAccordionIcon.svg";
import EditIcon from "../../assets/svg/Pencil.svg";
import Loader from "../../common/Loader/Loader";
import basicInformationStyles from "./BasicInformationStyles";

const BasicInformation = ({ loading, posDetails, isEditable = true, userInfo = {} }) => {
  const { storeName, posType, posNo, services, posContacts, posAddress } = posDetails;

  const classes = basicInformationStyles();

  const regions = useSelector(selectAllRegions);
  const areas = useSelector(selectAllAreas);

  const getAreaNameFromAreaId = (regionId, areaId) => {
    let areaObj = areas.find((a) => a.id === parseInt(areaId));
    return areaObj ? areaObj.name : "";
  };

  const renderServices = () => {
    if (isEmptyOrNull(services)) {
      return ""
    }

    return isArrayValid(services) ? services.map((s) => s?.displayName ?? s?.name ?? "").join(", ") : "";
  };

  const getPosContactsDetailsBasedOnKey = (key) => {
    if (isEmptyOrNull(posContacts) || !isArrayValid(posContacts)) {
      return "-";
    }

    const firstContact = posContacts?.[0]
    if (!isEmptyOrNull(firstContact) && !isEmptyOrNull(firstContact[key])) {
      return firstContact[key]
    }
    return "-";
  }

  const renderAccountSummary = () => {
    return (
      <AccordionSummary expandIcon={<img src={ExpandMoreIcon} alt="Expand Accordion Icon" />} className={classes.accordionSummary}>
        <Grid container alignItems="center">
          <Grid item xs={10}>
            {`${STORE_SERVICE_LABELS.POS_DETAILS}`}
          </Grid>
          {isEditable && is_basic_details_edit_visible(userInfo) && (
            <Grid item xs={2}>
              <Grid container>
                <Grid item xs={10}></Grid>
                <Grid item xs={2}>
                  <Link to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.BASIC_DETAILS}/${posNo}`} className={classes.dFlex}>
                    <img src={EditIcon} alt="Expand Accordion Icon" />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
    )
  }

  return (
    <Accordion defaultExpanded className={classes.accordion}>
      {renderAccountSummary()}
      <AccordionDetails className={classes.accordionDetails}>
        {loading ? (
          <Loader />
        ) : (
          <Grid container>
            <Grid item>
              <Typography className={classes.primaryInfoTitle}>{`${STORE_SERVICE_LABELS.PRIMARY_INFORMATION}`}</Typography>
            </Grid>
            <Grid container className={classes.informationContainer}>
              <ViewBasicDetailNameAndValue classes={classes} fieldTitle={`${STORE_SERVICE_LABELS.POS_NUMBER}`} fieldData={posNo} />
              <ViewBasicDetailNameAndValue classes={classes} fieldTitle={`${STORE_SERVICE_LABELS.POS_NAME}`} fieldData={storeName} />
              <ViewBasicDetailNameAndValue classes={classes} fieldTitle={`${STORE_SERVICE_LABELS.POS_TYPE}`} fieldData={posType} />
              <ViewBasicDetailNameAndValue classes={classes} fieldTitle={`${STORE_SERVICE_LABELS.SERVICE_TYPES}`} fieldData={services && renderServices()} />
            </Grid>
            <div className={classes.horizontalLine} />
            <Grid container>
              <Grid item>
                <Typography className={classes.primaryInfoTitle}>{`${STORE_SERVICE_LABELS.ADDRESS_INFORMATION}`}</Typography>
              </Grid>
              <Grid container className={classes.informationContainer}>
                <ViewBasicDetailNameAndValue
                  classes={classes}
                  fieldTitle={`${STORE_SERVICE_LABELS.NAME}`}
                  fieldData={getPosContactsDetailsBasedOnKey("name")}
                />
                <ViewBasicDetailNameAndValue
                  classes={classes}
                  fieldTitle={`${STORE_SERVICE_LABELS.EMAIL}`}
                  fieldData={getPosContactsDetailsBasedOnKey("email")}
                />
                <ViewBasicDetailNameAndValue
                  classes={classes}
                  fieldTitle={`${STORE_SERVICE_LABELS.PHONE_NUMBER}`}
                  fieldData={getPosContactsDetailsBasedOnKey("phoneNumber")}
                />
                <ViewBasicDetailNameAndValue
                  classes={classes}
                  fieldTitle={`${STORE_SERVICE_LABELS.ADDRESS}`}
                  fieldData={
                    posAddress &&
                    getFullAddress({
                      ...posAddress,
                      region: getRegionNameFromRegionId(posAddress?.region, regions),
                      area: getAreaNameFromAreaId(posAddress?.region, posAddress?.area),
                    })
                  }
                />
                <ViewBasicDetailNameAndValue
                  classes={classes}
                  fieldTitle={`${STORE_SERVICE_LABELS.COORDINATES}`}
                  fieldData={
                    posAddress &&
                    getCoordinatesFromAddress(posAddress)
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

BasicInformation.propTypes = {
  loading: PropTypes.bool.isRequired,
  posDetails: PropTypes.shape({
    storeName: PropTypes.string,
    posType: PropTypes.string,
    posNo: PropTypes.string,
    services: PropTypes.array,
    posContacts: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
    })),
    posAddress: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string,
      region: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  isEditable: PropTypes.bool,
  userInfo: PropTypes.object,
};

export default React.memo(BasicInformation);